import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      toast.success("Password updated. You can now sign in.");
      navigate("/admin/login");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not reset password. Request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F4EC] flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="max-w-sm w-full bg-[#FFFDF9] rounded-2xl border border-[rgba(58,42,37,0.08)] shadow-[0_20px_50px_rgba(58,42,37,0.08)] p-10">
        <p className="font-serif-display text-2xl tracking-[0.25em] text-[#3A2A25] text-center">CELUSTA</p>
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C9A45C] text-center mt-2 mb-8">New Password</p>
        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New password (min. 8 characters)"
            required
            className="w-full bg-[#F8F4EC] border border-[rgba(58,42,37,0.15)] rounded-lg px-4 py-3 text-sm text-[#2B1F1B] placeholder:text-[#9A8D85] focus:outline-none focus:border-[#3A2A25] transition-colors duration-300"
            data-testid="reset-password-input"
          />
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            required
            className="w-full bg-[#F8F4EC] border border-[rgba(58,42,37,0.15)] rounded-lg px-4 py-3 text-sm text-[#2B1F1B] placeholder:text-[#9A8D85] focus:outline-none focus:border-[#3A2A25] transition-colors duration-300"
            data-testid="reset-confirm-input"
          />
          {error && <p className="text-xs text-[#C62828]" data-testid="reset-password-error">{error}</p>}
          <button
            type="submit"
            disabled={loading || !token}
            className="w-full px-8 py-3.5 bg-[#3A2A25] text-white text-xs font-semibold uppercase tracking-[0.2em] rounded-full hover:bg-[#55423A] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="reset-submit-button"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            Set New Password
          </button>
        </form>
        <Link to="/admin/login" className="block text-center mt-6 text-xs text-[#C9A45C] hover:underline">
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
